
import {
  TBaseTypographyProps,
  TTypographyAlign,
  TTypographyAlignSubsetProp,
  TTypographyColor,
  TTypographyTag,
  TTypographyWhitespace,
  BASE_TYPOGRAPHY_DEFAULTS,
} from "@/ui/components/typography/typography.config";
import {
  DefinedDefaults,
  SubsetKeys,
  TWithDataComponent,
  subsetOf,
} from "@/ui/lib/types";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const AVAILABLE_TEXT_CONTROL_TAGS = subsetOf<TTypographyTag>()([ "label", "span", "div", "strong" ]);

export const AVAILABLE_TEXT_CONTROL_COLORS =
  subsetOf<TTypographyColor>()([ "default", "inherit" ]);

export const AVAILABLE_TEXT_CONTROL_ALIGNMENTS =
  subsetOf<TTypographyAlign>()([ "left", "center" ]);

export const AVAILABLE_TEXT_CONTROL_WHITESPACE =
  subsetOf<TTypographyWhitespace>()([ "normal", "noWrap" ]);

export const BASE_TEXT_CONTROL_SIZE_CLASS_MAP = {
  1: "font-sys-display font-bold uppercase text-sys-xl leading-sys-1-3",
  2: "font-sys-display font-bold text-sys-base leading-sys-1",
  3: "font-sys-display font-bold text-sys-sm leading-sys-1",
  4: "font-sys-default uppercase text-sys-xs leading-sys-1-3",
} as const;

export const RESPONSIVE_TEXT_CONTROL_SIZE_CLASS_MAP = {
  1: `${BASE_TEXT_CONTROL_SIZE_CLASS_MAP[1]} xl:text-sys-4xl`,
  2: `${BASE_TEXT_CONTROL_SIZE_CLASS_MAP[2]} xl:text-sys-2xl`,
  3: `${BASE_TEXT_CONTROL_SIZE_CLASS_MAP[3]} xl:text-sys-xl`,
  4: `${BASE_TEXT_CONTROL_SIZE_CLASS_MAP[4]} xl:text-sys-base`,
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTextControlTag = SubsetKeys<typeof AVAILABLE_TEXT_CONTROL_TAGS>;
export type TTextControlColor = SubsetKeys<typeof AVAILABLE_TEXT_CONTROL_COLORS>;
export type TTextControlAlign = SubsetKeys<typeof AVAILABLE_TEXT_CONTROL_ALIGNMENTS>;
export type TTextControlWhitespace = SubsetKeys<typeof AVAILABLE_TEXT_CONTROL_WHITESPACE>;
export type TTextControlSize = keyof typeof BASE_TEXT_CONTROL_SIZE_CLASS_MAP;

export type TTextControlProps = TBaseTypographyProps & TWithDataComponent & {
  color?: TTextControlColor;
  align?: TTypographyAlignSubsetProp<TTextControlAlign>;
  tag?: TTextControlTag;
  whitespace?: TTextControlWhitespace;
  size?: TTextControlSize;
};

type TTextControlDefaultProps = DefinedDefaults<
  TTextControlProps,
  "color" | "align" | "tag" | "whitespace" | "size" | "data-component"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const TEXT_CONTROL_DEFAULTS = {
  ...BASE_TYPOGRAPHY_DEFAULTS,
  "data-component": "TextControl",
  color: "default",
  align: "left",
  tag: "span",
  whitespace: "normal",
  size: 2,
} satisfies TTextControlDefaultProps;
